import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import BarcodeFields from "../../field/barcode/BarcodeFields";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import BarcodeHelper from "../../../../shared/helper/BarcodeHelper";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import DocumentSettings from "../../../document/DocumentSettings";
import MimeType from "../../../document/mimetype/MimeType";
import PageRange from "../../../../shared/components/PageRange";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import BarcodeDialog, {BarcodeDialogProperties, BarcodeDialogStatus} from "./BarcodeDialog";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {
	Barcode,
	Information,
	Settings,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalBarcode} from "../../../../shared/models/PortalBarcode";
import {PortalAddBarcode, PortalBarcodeFormat} from "../../../../shared/models/PortalAddBarcode";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface BarcodeCreateAreaDialogProperties extends BarcodeDialogProperties {
}

interface BarcodeCreateAreaDialogStatus extends BarcodeDialogStatus {
	pages: number;
	currentPage: string;
}

export class BarcodeCreateAreaDialogDefinition extends BarcodeDialog<BarcodeCreateAreaDialogProperties,
	BarcodeCreateAreaDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1"
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return super.isEnabled(portalInfo);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.BARCODE_CREATE_AREA, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	protected renderFormatOptions(): ReactNode {
		let parameter: PortalBarcode | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: PortalAddBarcode = parameter!.add as PortalAddBarcode;

		return (
			<>
				<div className="p-col-12 p-lg-6 p-grid" hidden={
					!this.state.fields?.contains(BarcodeFields.FORMAT)
				}>
					<label className="p-col-12 p-sm-5" htmlFor="control-format">
						<Translate {...this.props} data="barcode.format.label" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-7 p-grid">
						<Dropdown inputId="control-format"
								  value={FunctionHelper.applyValue(
									  addParameter.format, PortalAddBarcode.getFormatDefault()
								  )}
								  options={[
									  {
										  label: Translate.value("barcode.format.qrcode", {ns: "dialog"}),
										  value: PortalBarcodeFormat.qrcode
									  },
									  {
										  label: Translate.value("barcode.format.aztec", {ns: "dialog"}),
										  value: PortalBarcodeFormat.aztec
									  },
									  {
										  label: Translate.value("barcode.format.codabar", {ns: "dialog"}),
										  value: PortalBarcodeFormat.codabar
									  },
									  {
										  label: Translate.value("barcode.format.code128", {ns: "dialog"}),
										  value: PortalBarcodeFormat.code128
									  },
									  {
										  label: Translate.value("barcode.format.code39", {ns: "dialog"}),
										  value: PortalBarcodeFormat.code39
									  },
									  {
										  label: Translate.value(
											  "barcode.format.datamatrix", {ns: "dialog"}
										  ),
										  value: PortalBarcodeFormat.datamatrix
									  },
									  {
										  label: Translate.value("barcode.format.ean13", {ns: "dialog"}),
										  value: PortalBarcodeFormat.ean13
									  },
									  {
										  label: Translate.value("barcode.format.ean8", {ns: "dialog"}),
										  value: PortalBarcodeFormat.ean8
									  },
									  {
										  label: Translate.value("barcode.format.itf", {ns: "dialog"}),
										  value: PortalBarcodeFormat.itf
									  },
									  {
										  label: Translate.value("barcode.format.pdf417", {ns: "dialog"}),
										  value: PortalBarcodeFormat.pdf417
									  },
									  {
										  label: Translate.value("barcode.format.upca", {ns: "dialog"}),
										  value: PortalBarcodeFormat.upca
									  }
								  ]}
								  onChange={(e) => this.onChange(BarcodeFields.FORMAT, e.value)}
								  appendTo={document.getElementById("root")}/>
					</div>
				</div>
			</>
		);
	}

	protected renderContentOptions(): ReactNode {
		let parameter: PortalBarcode | undefined = this.state.parameter?.data;
		let errors: Map<BarcodeFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: PortalAddBarcode = parameter!.add as PortalAddBarcode;
		let content: string = addParameter.getFormatContent();

		return (
			<fieldset className="p-col-12 p-grid" hidden={!this.state.fields?.contains(BarcodeFields.CONTENT)}>
				<div className="p-col-12 p-grid" hidden={
					addParameter.format === PortalBarcodeFormat.itf ||
					addParameter.format === PortalBarcodeFormat.ean8 ||
					addParameter.format === PortalBarcodeFormat.ean13 ||
					addParameter.format === PortalBarcodeFormat.codabar ||
					addParameter.format === PortalBarcodeFormat.upca
				}>
					<label className="p-col-12 p-sm-5 large-label" htmlFor="control-content-textarea">
						<Translate {...this.props} data="barcode.create.content" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-7 large-input p-grid">
						<InputTextarea id="control-content-textarea" rows={3} value={content}
									   className={errors.has(BarcodeFields.CONTENT) ? "p-invalid" : ""}
									   tooltip={errors.has(BarcodeFields.CONTENT) ? Translate.value(
										   errors.get(BarcodeFields.CONTENT)!, {ns: "message"}
									   ) : ""}
									   onInput={(e) => this.onChange(
										   BarcodeFields.CONTENT, e.currentTarget.value
									   )}/>
						{this.renderFieldError(errors.has(BarcodeFields.CONTENT) ?
							Translate.value(
								errors.get(BarcodeFields.CONTENT)!, {ns: "message"}
							) : ""
						)}
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid" hidden={
					addParameter.format !== PortalBarcodeFormat.itf &&
					addParameter.format !== PortalBarcodeFormat.ean8 &&
					addParameter.format !== PortalBarcodeFormat.ean13 &&
					addParameter.format !== PortalBarcodeFormat.codabar &&
					addParameter.format !== PortalBarcodeFormat.upca
				}>
					<label className="p-col-12 p-sm-5" htmlFor="control-content-text">
						<Translate {...this.props} data="barcode.create.content" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-7 p-grid">
						<InputText id="control-content-text" value={content}
								   className={errors.has(BarcodeFields.CONTENT) ? "p-invalid" : ""}
								   tooltip={errors.has(BarcodeFields.CONTENT) ? Translate.value(
									   errors.get(BarcodeFields.CONTENT)!, {ns: "message"}
								   ) : ""}
								   onInput={(e) => this.onChange(
									   BarcodeFields.CONTENT, e.currentTarget.value
								   )}/>
						{this.renderFieldError(errors.has(BarcodeFields.CONTENT) ? Translate.value(
							errors.get(BarcodeFields.CONTENT)!, {ns: "message"}
						) : "")}
					</div>
				</div>
			</fieldset>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalBarcode | undefined = this.state.parameter?.data;
		let errors: Map<BarcodeFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: PortalAddBarcode = parameter!.add as PortalAddBarcode;
		let pages: string = addParameter.getFormatPages();

		return (
			<>
				<div className="p-col-12 p-xl-8 p-grid divider-right-hidden">
					{this.renderContentOptions()}

					<fieldset className="p-col-12 p-grid">
						{this.renderFormatOptions()}
						{this.renderQrcodeOptions()}
						{this.renderAztecOptions()}
						{this.renderDatamatrixOptions()}
						{this.renderPdf417Options()}
						{this.renderGeneralOptions()}
					</fieldset>

					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							!this.state.fields?.contains(BarcodeFields.PAGERANGE)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-pagerange">
								<Translate {...this.props} data="barcode.pages.label" namespace="dialog"/>
								<Info {...this.props} data="barcode.pages.info" options={{ns: "dialog"}}/>
							</label>
							<div className="p-col-12 p-sm-7">
								<PageRange inputId="control-pagerange" value={pages}
										   className={errors.has(BarcodeFields.PAGERANGE) ? " p-invalid" : ""}
										   onChange={(e) => this.onChange(
											   BarcodeFields.PAGERANGE, e.value
										   )}
										   appendTo={document.getElementById("root")} editable={true}
										   addon={Translate.value(
											   "inputs.pagerange.of", {pages: this.state.pages}
										   )}
										   tooltip={errors.has(BarcodeFields.PAGERANGE) ? Translate.value(
											   errors.get(BarcodeFields.PAGERANGE)!, {ns: "message"}
										   ) : ""}
										   options={[
											   {
												   label: Translate.value("inputs.pagerange.all"),
												   value: PAGERANGE_ALL
											   },
											   {
												   label: Translate.value("inputs.pagerange.current"),
												   value: this.state.currentPage
											   }
										   ]}/>
								{this.renderFieldError(errors.has(BarcodeFields.PAGERANGE) ?
									Translate.value(
										errors.get(BarcodeFields.PAGERANGE)!, {ns: "message"}
									) : ""
								)}
							</div>
						</div>
					</fieldset>
				</div>

				{this.renderPreview()}
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["create-barcode-dialog", "verylarge"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<>
				<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
						appendTo={document.getElementById("root")} resizable={false}
						closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}>
					<div className="p-grid">
						{this.renderError()}
						<Helper {...this.props}>
							<Translate {...this.props} data={"dialog.barcode.area.general"} namespace={"help"}
									   markdown={true}/>
						</Helper>
						{this.renderContents()}
					</div>
				</Dialog>
			</>
		);
	}

	protected async onChange(field: BarcodeFields, value: any): Promise<void> {
		await super.onChange(field, value);

		let parameter: PortalBarcode = this.state.parameter!.data;
		let addParameter: PortalAddBarcode = parameter.add as PortalAddBarcode;
		let errors: Map<BarcodeFields, string> = this.state.fieldErrors;
		let hasError: boolean = false;

		switch (field) {
			case BarcodeFields.FORMAT:
				await this.onChange(BarcodeFields.PAGERANGE, addParameter.getFormatPages());
				break;
			case BarcodeFields.CONTENT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
					hasError = true;
				}

				switch (addParameter.format) {
					case PortalBarcodeFormat.aztec:
						addParameter.aztec![0].value = value;
						break;
					case PortalBarcodeFormat.codabar:
						if (!BarcodeHelper.checkCodabarBoundary(value)) {
							errors.set(field, "errors.barcode.codabar_boundaries");
							hasError = true;
						} else if (!(await BarcodeHelper.isCodabar(value))) {
							errors.set(field, "errors.barcode.codabar");
							hasError = true;
						}

						addParameter.codabar![0].value = value;
						break;
					case PortalBarcodeFormat.code39:
						if (!(await BarcodeHelper.isCode39(value))) {
							errors.set(field, "errors.barcode.code39");
							hasError = true;
						}

						addParameter.code39![0].value = value;
						break;
					case PortalBarcodeFormat.code128:
						addParameter.code128![0].value = value;
						break;
					case PortalBarcodeFormat.datamatrix:
						addParameter.datamatrix![0].value = value;
						break;
					case PortalBarcodeFormat.ean8:
						if (!(await BarcodeHelper.isEan8(value))) {
							errors.set(field, "errors.barcode.ean8");
							hasError = true;
						}

						addParameter.ean8![0].value = value;
						break;
					case PortalBarcodeFormat.ean13:
						if (!(await BarcodeHelper.isEan13(value))) {
							errors.set(field, "errors.barcode.ean13");
							hasError = true;
						}

						addParameter.ean13![0].value = value;
						break;
					case PortalBarcodeFormat.itf:
						if (!(await BarcodeHelper.isItf(value))) {
							errors.set(field, "errors.barcode.itf");
							hasError = true;
						}

						addParameter.itf![0].value = value;
						break;
					case PortalBarcodeFormat.pdf417:
						addParameter.pdf417![0].value = value;
						break;
					case PortalBarcodeFormat.qrcode:
						addParameter.qrcode![0].value = value;
						break;
					case PortalBarcodeFormat.upca:
						if (!(await BarcodeHelper.isUpca(value))) {
							errors.set(field, "errors.barcode.upca");
							hasError = true;
						}

						addParameter.upca![0].value = value;
						break;
				}

				if (!hasError && errors.has(field)) {
					errors.delete(field);
				}
				break;
			case BarcodeFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				switch (addParameter.format) {
					case PortalBarcodeFormat.aztec:
						addParameter.aztec![0].pages = value;
						break;
					case PortalBarcodeFormat.codabar:
						addParameter.codabar![0].pages = value;
						break;
					case PortalBarcodeFormat.code39:
						addParameter.code39![0].pages = value;
						break;
					case PortalBarcodeFormat.code128:
						addParameter.code128![0].pages = value;
						break;
					case PortalBarcodeFormat.datamatrix:
						addParameter.datamatrix![0].pages = value;
						break;
					case PortalBarcodeFormat.ean8:
						addParameter.ean8![0].pages = value;
						break;
					case PortalBarcodeFormat.ean13:
						addParameter.ean13![0].pages = value;
						break;
					case PortalBarcodeFormat.itf:
						addParameter.itf![0].pages = value;
						break;
					case PortalBarcodeFormat.pdf417:
						addParameter.pdf417![0].pages = value;
						break;
					case PortalBarcodeFormat.qrcode:
						addParameter.qrcode![0].pages = value;
						break;
					case PortalBarcodeFormat.upca:
						addParameter.upca![0].pages = value;
						break;
				}
				break;
			case BarcodeFields.OUTPUT:
				addParameter.outputFormat = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter,
			previewBuild: false
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-barcode-create"}/>
					<Translate {...this.props} data="barcode.create.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalBarcode | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let pageCount: number = await portalDocument.getNumberOfPages();
		let currentPage: number = documentSettings.activeViewer.currentPage;

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			currentPage: currentPage.toString(),
			pages: pageCount
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let operationData: PortalBarcode = this.cleanupOperationData();
		let webServiceType: WebServiceType = WebServiceTypes.BARCODE;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: Barcode.fromJson(operationData.toJson()),
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as Settings)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.barcode.create", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
		}

		this.resetMouseMode(documentManager.getSelection());
		this.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.BARCODE_CREATE_AREA;
	}
}

const BarcodeCreateAreaDialog = React.memo(BarcodeCreateAreaDialogDefinition,
	(
		prevProps: Readonly<BarcodeCreateAreaDialogProperties>, nextProps: Readonly<BarcodeCreateAreaDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default BarcodeCreateAreaDialog;